/**
 * Clinician Coach Quality dashboard — surfaces recent AI Coach replies
 * that patients marked as unhelpful, alongside their "What went wrong?"
 * notes. Powered by GET /api/coach/feedback/quality which joins
 * db.coach_feedback (helpful=false) with the underlying coach_messages
 * row + the patient's name.
 *
 * This is the first place clinicians can spot systematic gaps in the AI
 * coach's advice (e.g. a specific exercise the AI keeps hallucinating
 * regressions for). It stays lightweight — no filters yet, just the
 * newest 50 down-votes with the exact AI reply + optional note.
 */
import { useEffect, useState } from "react";
import DashLayout from "@/components/DashLayout";
import SortableGrid from "@/components/SortableGrid";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";
import api from "@/lib/api";
import { ThumbsDown, MessageSquare, User as UserIcon, Loader2, RefreshCw, GripVertical } from "lucide-react";

function relativeTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function ClinicianCoachQuality() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_down: 0, total_up: 0 });
  const { editing: editLayout } = useEditMode();
  const {
    visibleOrder: statOrder, setOrder: setStatOrder,
    toggleHidden: hideStat, resetToDefault: resetStat,
    setSize: sizeStat, sizes: statSizes,
    isDirty: statDirty,
  } = useDashboardLayout("clinician_coach_quality_stats", ["down", "up", "ratio"]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/coach/feedback/quality");
      setItems(r.data?.items || []);
      setStats(r.data?.stats || { total_down: 0, total_up: 0 });
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ratio = stats.total_up + stats.total_down > 0
    ? `${Math.round(100 * stats.total_up / (stats.total_up + stats.total_down))}%`
    : "—";
  const STAT_CARDS = {
    down: (
      <div className="fl-card p-4 h-full" data-testid="coach-quality-stat-down">
        <div className="fl-label" style={{ color: "#FCA5A5" }}>👎 unhelpful</div>
        <div className="text-3xl font-bold mt-1">{stats.total_down}</div>
      </div>
    ),
    up: (
      <div className="fl-card p-4 h-full" data-testid="coach-quality-stat-up">
        <div className="fl-label" style={{ color: "#86EFAC" }}>👍 helpful</div>
        <div className="text-3xl font-bold mt-1">{stats.total_up}</div>
      </div>
    ),
    ratio: (
      <div className="fl-card p-4 h-full" data-testid="coach-quality-stat-ratio">
        <div className="fl-label" style={{ color: "var(--fl-muted)" }}>helpful ratio</div>
        <div className="text-3xl font-bold mt-1">{ratio}</div>
      </div>
    ),
  };

  return (
    <DashLayout role="clinician" title="Coach Quality">
      <div className="max-w-5xl mx-auto px-6 py-6" data-testid="coach-quality-page">
        <div className="flex items-center gap-3 mb-4">
          <ThumbsDown size={20} className="text-[#f47a20]" />
          <h1 className="fl-heading text-2xl">Coach quality</h1>
          <div className="ml-auto flex items-center gap-2">
            {editLayout && statDirty && (
              <button type="button" onClick={resetStat}
                      className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-white/10"
                      data-testid="coach-quality-layout-reset">Reset</button>
            )}
            <button type="button" onClick={() => {}}
                    className="hidden"
                    data-testid="coach-quality-edit-toggle">
              <GripVertical size={12} />
              {editLayout ? "Done" : "Edit"}
            </button>
            <button type="button" onClick={load}
                    className="fl-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid="coach-quality-refresh">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--fl-muted)" }}>
          The last 50 AI Coach replies your patients marked as unhelpful. Use these to spot patterns — a specific exercise the coach keeps mis-cueing, a topic where it drifts, or a language the patient prefers.
        </p>

        {/* iter101 · rearrangeable stat strip */}
        <SortableGrid
          items={statOrder}
          onReorder={setStatOrder}
          onHide={hideStat}
          onResize={sizeStat}
          sizes={statSizes}
          editMode={editLayout}
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6"
        >
          {statOrder.map((id) => (
            <SortableGrid.Item key={id} id={id}>
              {STAT_CARDS[id]}
            </SortableGrid.Item>
          ))}
        </SortableGrid>

        {loading && (
          <div className="fl-card p-6 flex items-center justify-center" data-testid="coach-quality-loading">
            <Loader2 className="animate-spin" size={18} />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="fl-card p-8 text-center" style={{ color: "var(--fl-muted)" }}
               data-testid="coach-quality-empty">
            No unhelpful feedback yet — the coach is doing well. Patients can rate any reply from the /app/coach page.
          </div>
        )}

        <div className="space-y-3">
          {items.map((row) => (
            <div key={row.id}
                 className="fl-card p-4"
                 data-testid={`coach-quality-row-${row.id}`}>
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <UserIcon size={12} className="text-[color:var(--fl-orange)]" />
                <span style={{ color: "var(--fl-text)" }}>{row.patient_name || "Patient"}</span>
                <span style={{ color: "var(--fl-muted-2)" }}>· {relativeTime(row.created_at)}</span>
                {row.message_source && row.message_source !== "patient_chat" && (
                  <span className="ml-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}>
                    {row.message_source.replace("_", " ")}
                  </span>
                )}
              </div>
              <div className="mt-2 p-3 rounded-md text-sm leading-relaxed whitespace-pre-wrap"
                   style={{ background: "var(--fl-bg-2)", color: "var(--fl-text-2)" }}>
                {row.message_content || "—"}
              </div>
              {row.note && (
                <div className="mt-2 flex items-start gap-2 p-3 rounded-md text-sm"
                     style={{ background: "color-mix(in oklab, #EF4444 10%, transparent)",
                              border: "1px solid color-mix(in oklab, #EF4444 30%, transparent)",
                              color: "#FCA5A5" }}
                     data-testid={`coach-quality-note-${row.id}`}>
                  <MessageSquare size={13} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest mb-1">What went wrong</div>
                    <div>{row.note}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashLayout>
  );
}
